import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy, signal, WritableSignal } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { distinctUntilChanged, lastValueFrom, takeUntil } from "rxjs";
import { SharedModule } from "../../shared.module";
import { ActionButtonComponent } from "../ui-components/buttons/action-button/action-button.component";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { DropdownComponent } from "../ui-components/dropdown/dropdown.component";
import { DropdownOption } from "../ui-components/dropdown/dropdown.model";
import { SobitService } from "src/app/services/sobit.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { IBenefitsItem, IGetPaymentTypesResultData, SharedService } from "@dert-services";
import { isNullOrUndefined } from "src/app/functions/common-functions";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { OfflinePaymentDialogContext, PaymentDialogResult } from "src/app/models/payment-dialog.model";
import { IPaymentAttribute } from "src/app/services/model/pos.api.model";
import { AuthorizationPolicy, isAuthorizedForPolicy } from "src/app/guards/authorization-policy";
import { SKODA_PAYMENT_NAME } from "src/app/models/payment-availability.model";

@Component({
  selector: "app-others-payment-dialog",
  templateUrl: "./others-payment-dialog.component.html",
  styleUrls: ["./others-payment-dialog.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    ActionButtonComponent,
    FormInputComponent,
    DropdownComponent,
    FontAwesomeModule,
    FormContainerComponent,
  ],
  standalone: true,
})
export class OthersPaymentDialogComponent extends PaymentDialogBase implements OnDestroy {

  private static readonly SKODA_RETURN_CODE_ATTRIBUTE = "SkodaReturnCode";
  private static readonly SKODA_RETURN_CODES_CONFIG = "SkodaReturnCodes";
  private static readonly SKODA_PREFILLED_RETURN_CODES = [
    "SKODA_RETURN",
    "SKODA_SK_RETURN",
  ];

  private static readonly OVERPAYMENT_SUPPORTED_TENDER_TYPES = [
    TenderType.Cash,
    TenderType.Card,
    TenderType.Virtual,
    TenderType.ElectronicVoucher,
  ];

  private readonly _sobitService = inject(SobitService);

  private readonly mallIdentifier = 'VOUCHER OC';
  private _otherPaymentTypes: IGetPaymentTypesResultData[] = [];
  private _defaultMaxBenefitPaymentValue = 0;
  
  public selectedPaymentsIds: string[] = [];
  public paymentType: IGetPaymentTypesResultData;
  public mallOptions: DropdownOption[] = [];
  public allBenefitsOptions: DropdownOption[] = [];
  public tenderTypes = TenderType;
  public selectedBenefitType: TenderType | null = null;
  public selectedBenefitTypeNominalsOption: DropdownOption[] | null = null;
  public maxBenefitPaymentValue: number = 0;
  public leftToPay: number = 0;
  public selectedPreFilter: TenderType | null = null;
  public preFilterOptions: DropdownOption[] = [];
  public skodaReturnCodeOptions: DropdownOption[] = [];

  public showSubTypeDropdown: WritableSignal<boolean> = signal(false);

  public confirmForm = new FormGroup({
    amountToPay: new FormControl<number>(null),
    selectAllCheckbox: new FormControl<boolean>(false),
    selectedBenefit: new FormControl<string>(null),
    selectedMallVoucherType: new FormControl<string>(null),
    preFilter: new FormControl<TenderType | null>(null),
    valueToPay: new FormControl<number>(null, [
      Validators.required,
      Validators.min(0),
      this.validateMaxValue(),
      this.validateSkodaReturnAmount(),
    ]),
    voucherCode: new FormControl<string>(null),
    voucherRowsGroup: new FormGroup({
      rows: new FormArray([], Validators.nullValidator)
    }),
    confirmationCode: new FormControl<string>(null, {
        validators: [Validators.minLength(6)],
        asyncValidators: [],
        updateOn: 'change'
      }),
    paymentType: new FormControl<TenderType>(null),
    paymentName: new FormControl<string>(null),
    skodaReturnCode: new FormControl<string>(null),
    allowOverpayment: new FormControl<boolean>(false),
    overpaymentReason: new FormControl<string | null>(null),
  });

  get tenderType(): TenderType {
    return this.selectedBenefitType || TenderType.ElectronicVoucher;
  }

  // ElectronicVoucher represents an "Other" payment processed by the card terminal,
  // including VOUCHER OC CARD / MALLCARD. Manual entry uses the cancellation role set.
  get canOpenOfflinePayment(): boolean {
    return !this.isReturn
      && this.selectedBenefitType === TenderType.ElectronicVoucher
      && isAuthorizedForPolicy(this.loginService.user?.Roles, AuthorizationPolicy.Cancellation);
  }
  
  protected getPaymentTranslationKey(): string {
    return 'webPos.Benefits';
  }

  public override canUseOverpaymentOverride(): boolean {
    return !this.isReturn
      && !!this.paymentType
      && (
        this.paymentType.IsBenefit !== true
        || OthersPaymentDialogComponent.OVERPAYMENT_SUPPORTED_TENDER_TYPES.includes(this.selectedBenefitType)
      );
  }
  
  protected isFormValid(): boolean {
    return !this.isBenefitFormInvalid();
  }
  
  async ngOnInit() {
    const benefitTypes = this.filterAvailablePayments(await this._sharedService.getOtherTypes());
    this._otherPaymentTypes = benefitTypes || [];

    const benefitAll = await this._sharedService.getFormattedBenefitData(benefitTypes);

    this.allBenefitsOptions = benefitAll.filter(b => !b.value.toUpperCase().includes(this.mallIdentifier));
    const mallVouchersLabel = await lastValueFrom(this.translate.get('webPos.MallVouchers'));
    if (benefitAll.some(b => b.value.toUpperCase().includes(this.mallIdentifier))) {
      this.allBenefitsOptions.push({ value: this.mallIdentifier, label: mallVouchersLabel });
    }

    this.mallOptions = benefitAll.filter(b => b.value.toUpperCase().includes(this.mallIdentifier));
    this.leftToPay = this.data.remainingToPay;

    await this.setMaximumBenefitPayment();
    this.setupFormSubscriptions();

    // RxJS: react to pre-filter changes
    this.confirmForm.controls.preFilter.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged()
    ).subscribe(value => this.handlePreFilterChange(value as TenderType | null));

    // RxJS: react to benefit selection
    this.confirmForm.controls.selectedBenefit.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value) {
        this.handleBenefitSelection(value);
      }
    });

    // RxJS: when mall voucher subtype is selected, apply the real payment type
    this.confirmForm.controls.selectedMallVoucherType.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value && this.showSubTypeDropdown()) {
        this.handleBenefitSelection(value);
      }
    });

    if (this.allBenefitsOptions.length === 1) {
      const onlyOption = this.allBenefitsOptions[0];
      this.confirmForm.controls.selectedBenefit.setValue(onlyOption.value);
      if (this.showSubTypeDropdown() && this.mallOptions.length === 1) {
        this.confirmForm.controls.selectedMallVoucherType.setValue(this.mallOptions[0].value);
      }
    }

    this.confirmForm.controls.allowOverpayment.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged()
    ).subscribe(() => {
      this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });
    });

    this.confirmForm.controls.skodaReturnCode.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged()
    ).subscribe(value => this.handleSkodaReturnCodeSelection(value));

    // RxJS: keep valueToPay in sync with voucher rows total
    this.confirmForm.controls.voucherRowsGroup.valueChanges.pipe(
      takeUntil(this.destroyObservable$)
    ).subscribe(() => {
      if (this.selectedBenefitType === this.tenderTypes.Cheque &&
          this.selectedBenefitTypeNominalsOption?.length > 0) {
        const total = this.getTotalPaperValue();
        this.confirmForm.controls.valueToPay.setValue(total, { emitEvent: false });
      }
    });
  }

  public async onConfirm(openOfflinePayment = false) {
    // Hiding the action is not authorization; reject programmatic calls as well.
    if (openOfflinePayment && !this.canOpenOfflinePayment) {
      return;
    }

    // Standard terminal confirmation requires Sobit availability. Offline entry only
    // validates user input because it exists specifically for a disconnected terminal.
    const paymentSpecificFormInvalid = openOfflinePayment
      ? this.isOfflinePaymentFormInvalid()
      : this.isBenefitFormInvalid();

    if (this.confirmForm.invalid || paymentSpecificFormInvalid) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }

    const formValue = this.confirmForm.value;
    const paymentTypes = this.filterAvailablePayments(await this._sharedService.getOtherTypes());
    const benefitPaymentType = paymentTypes.find(pt => pt.Name === formValue.paymentName);
    if (!benefitPaymentType) return;

    this.dialogResult.PaymentAttributes ??= [];

    if (formValue.paymentType === TenderType.Cheque && !this.isSkodaReturnPayment) {
      const benefitPaymentItems: IBenefitsItem[] = formValue.voucherRowsGroup.rows.map((row: any) => ({
        Count: Number(row.numberOfItems) || 0,
        Value: Number(row.value) || 0,
      }));
      if (benefitPaymentItems.length > 0) {
        this.dialogResult.PaymentAttributes.push({ Key: 'Items', Value: benefitPaymentItems });
      }
    }

    if (formValue.paymentType === TenderType.Virtual && !this.isSkodaReturnPayment) {
      this.dialogResult.PaymentAttributes.push({ Key: 'ConfirmationCode', Value: formValue.confirmationCode });
    }

    if ((formValue.paymentType === TenderType.Voucher || formValue.paymentType === TenderType.Cheque)
        && !this.isSkodaReturnPayment) {
      this.dialogResult.PaymentAttributes.push({ Key: 'VoucherCode', Value: formValue.voucherCode });
    }

    if (this.isSkodaReturnPayment) {
      this.dialogResult.PaymentAttributes.push({
        Key: OthersPaymentDialogComponent.SKODA_RETURN_CODE_ATTRIBUTE,
        Value: formValue.skodaReturnCode,
      });
    }

    const dialogResult = this.dialogResult as PaymentDialogResult;
    const resolvedTenderType = benefitPaymentType?.TenderType ?? formValue.paymentType;
    const resolvedPaymentName = benefitPaymentType?.Name || "";
    const resolvedPaymentDescription = benefitPaymentType?.Description || resolvedPaymentName;

    dialogResult.Amount = Number(formValue.valueToPay);
    dialogResult.PaymentType = resolvedTenderType;
    dialogResult.PaymentName = resolvedPaymentName;
    dialogResult.PaymentDescription = resolvedPaymentDescription;
    dialogResult.Currency = this.loginService.user.CurrencyCode;

    if (resolvedTenderType === TenderType.ElectronicVoucher) {
      dialogResult.offlinePaymentDialogContext = this.getOfflinePaymentDialogContext(benefitPaymentType);

      // The parent closes this full-screen dialog before opening the offline form.
      if (openOfflinePayment) {
        dialogResult.openOfflineCardPaymentDialog = true;
      }
    }

    dialogResult.PaymentAttributes = this.appendOverpaymentAttributes(dialogResult.PaymentAttributes);

    this.dialogRef.close(dialogResult);
  }

  public async onOfflinePayment() {
    return this.onConfirm(true);
  }
  
  public async onSubmit() {
    return this.onConfirm();
  }

  public get rows(): FormArray {
    return this.confirmForm.controls.voucherRowsGroup.controls.rows;
  }

  private getOfflinePaymentDialogContext(
    paymentType: IGetPaymentTypesResultData | undefined,
  ): OfflinePaymentDialogContext {
    const resolvedTenderType = paymentType?.TenderType ?? this.tenderType;
    const resolvedPaymentName = paymentType?.Name ?? this.confirmForm.controls.paymentName.value ?? "";

    return {
      paymentType: resolvedTenderType,
      paymentName: resolvedPaymentName,
      paymentDisplayName: paymentType?.Description ?? resolvedPaymentName,
      currency: this.loginService.user.CurrencyCode,
      paymentAttributes: [
        { Key: "TenderType", Value: String(resolvedTenderType) },
        { Key: "TenderSubType", Value: paymentType?.TenderSubType ?? "" },
      ] satisfies IPaymentAttribute[],
    };
  }

  public async setMaximumBenefitPayment() {
    if (this.isReturn) {
      this.maxBenefitPaymentValue = isNullOrUndefined(this.leftToPay)
        ? this.amountToReturn
        : Math.abs(this.leftToPay);
      this._defaultMaxBenefitPaymentValue = this.maxBenefitPaymentValue;
      return;
    }

    let maxBenefitValue = (this.stateService.configuration?.maxBenefitPaymentValue || 20000) + 0;
    const maxBenefitPercentage = (this.stateService.configuration?.maxBenefitPaymentPercentage || 50) + 0;

    if (!isNullOrUndefined(this.data.totalPrice)) {
      const maxByPercentage = this.data.totalPrice * (maxBenefitPercentage / 100);
      if (maxByPercentage < maxBenefitValue) {
        maxBenefitValue = maxByPercentage;
      }
    }

    const benefitPaymentNames = (this.filterAvailablePayments(await this._sharedService.getOtherTypes()))
      .filter(pt => pt.IsBenefit).map(pt => pt.Name);

    let payedByBenefits = 0;
    payedByBenefits += this.data.payments
      .filter(item => benefitPaymentNames.includes(item.PaymentName) &&
        (item.Status === PaymentStatusEnum.Done || item.Status === PaymentStatusEnum.Pending))
      .reduce((sum, item) => sum + item.Amount, 0);
    
    maxBenefitValue -= payedByBenefits;

    if (this.leftToPay === null || this.leftToPay === undefined) {
      this.maxBenefitPaymentValue = maxBenefitValue;
      this._defaultMaxBenefitPaymentValue = this.maxBenefitPaymentValue;
      return;
    }

    this.maxBenefitPaymentValue = maxBenefitValue > this.leftToPay ? this.leftToPay : maxBenefitValue;
    this._defaultMaxBenefitPaymentValue = this.maxBenefitPaymentValue;
  }

  /** Handles pre-filter dropdown changes  driven by preFilter.valueChanges */
  private handlePreFilterChange(tenderTypeValue: TenderType | null) {
    this.selectedPreFilter = tenderTypeValue;
    this.selectedBenefitType = null;
    this.selectedBenefitTypeNominalsOption = null;
    this.resetOverpaymentOverride();

    // Remove rows first (before resetting validators/values)
    while (this.confirmForm.controls.voucherRowsGroup.controls.rows.length > 0) {
      this.confirmForm.controls.voucherRowsGroup.controls.rows.removeAt(0);
    }

    // Reset validators to defaults BEFORE clearing values to prevent cascading validation events
    this.confirmForm.controls.confirmationCode.setValidators([Validators.minLength(6)]);
    this.confirmForm.controls.voucherCode.setValidators([]);

    // Silence all value changes when resetting
    const silent = { emitEvent: false };
    this.confirmForm.controls.selectedBenefit.setValue(null, silent);
    this.confirmForm.controls.paymentName.setValue(null, silent);
    this.confirmForm.controls.paymentType.setValue(null, silent);
    this.confirmForm.controls.skodaReturnCode.setValue(null, silent);
    this.confirmForm.controls.valueToPay.setValue(null, silent);
    this.confirmForm.controls.voucherCode.setValue(null, silent);
    this.confirmForm.controls.confirmationCode.setValue(null, silent);
    this.confirmForm.controls.selectedMallVoucherType.setValue(null, silent);
    this.skodaReturnCodeOptions = [];
    if (this.isReturn) {
      this.restoreOverallReturnLimit();
    } else {
      this.leftToPay = this.data.remainingToPay;
      this.maxBenefitPaymentValue = this._defaultMaxBenefitPaymentValue;
    }
    this.updateSkodaReturnCodeValidation();
    this.showSubTypeDropdown.set(false);
    this.confirmForm.controls.confirmationCode.updateValueAndValidity({ emitEvent: false });
    this.confirmForm.controls.voucherCode.updateValueAndValidity({ emitEvent: false });
    this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });

    // Filter benefits list
    this.mallOptions = this.allBenefitsOptions.filter(b => b.value.toUpperCase().includes(this.mallIdentifier));
  }

  /** Handles benefit dropdown changes  driven by selectedBenefit.valueChanges */
  private handleBenefitSelection(paymentName: string) {
    const silent = { emitEvent: false };

    this.resetOverpaymentOverride();

    const selectedBenefit = (this.confirmForm.controls.selectedBenefit.value || "").toUpperCase();
    const isMallVoucherGroup = selectedBenefit.includes(this.mallIdentifier);
    this.showSubTypeDropdown.set(isMallVoucherGroup);

    const resolvedPaymentName = isMallVoucherGroup
      ? this.confirmForm.controls.selectedMallVoucherType.value
      : paymentName;

    if (!resolvedPaymentName) {
      this.selectedBenefitType = null;
      this.selectedBenefitTypeNominalsOption = null;
      this.paymentType = null;
      this.confirmForm.controls.paymentName.setValue(null, silent);
      this.confirmForm.controls.paymentType.setValue(null, silent);
      this.restoreOverallReturnLimit();
      return;
    }

    this.confirmForm.controls.valueToPay.setValue(null, silent);
    this.confirmForm.controls.voucherCode.setValue(null, silent);
    this.confirmForm.controls.confirmationCode.setValue(null, silent);

    this.selectedBenefitTypeNominalsOption = this._sharedService.getBenefitNominals(resolvedPaymentName, this._otherPaymentTypes);
    this.selectedBenefitType = this._sharedService.getBenefitType(resolvedPaymentName, this._otherPaymentTypes);
    this.paymentType = this._otherPaymentTypes.find(b => b.Name === resolvedPaymentName);

    if (!this.paymentType) {
      this.selectedBenefitType = null;
      this.selectedBenefitTypeNominalsOption = null;
      this.confirmForm.controls.paymentName.setValue(null, silent);
      this.confirmForm.controls.paymentType.setValue(null, silent);
      this.restoreOverallReturnLimit();
      return;
    }

    this.confirmForm.controls.paymentName.setValue(this.paymentType.Name, silent);
    this.confirmForm.controls.paymentType.setValue(this.selectedBenefitType, silent);

    this.updateSkodaReturnConfiguration();
    this.updateConfirmationCodeValidation();
    this.updateVoucherCodeValidation();
    this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });

    if (this.selectedBenefitType === this.tenderTypes.Cheque &&
        this.selectedBenefitTypeNominalsOption?.length > 0) {
      this.addRow();
    }
  }

  private updateConfirmationCodeValidation() {
    const ctrl = this.confirmForm.controls.confirmationCode;
    ctrl.setValidators(
      this.selectedBenefitType === this.tenderTypes.Virtual && !this.isSkodaReturnPayment
        ? [Validators.required, Validators.minLength(6)]
        : [Validators.minLength(6)]
    );
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateVoucherCodeValidation() {
    const ctrl = this.confirmForm.controls.voucherCode;
    ctrl.setValidators(
      this.selectedBenefitType === this.tenderTypes.Voucher && !this.isSkodaReturnPayment
        ? [Validators.required]
        : []
    );
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateSkodaReturnConfiguration(): void {
    const rawCodes = this.paymentType?.Attributes
      ?.find(attribute =>
        attribute.Key?.trim().toLowerCase()
        === OthersPaymentDialogComponent.SKODA_RETURN_CODES_CONFIG.toLowerCase()
      )
      ?.Value;

    const configuredCodes = this.parseSkodaReturnCodes(rawCodes);

    this.skodaReturnCodeOptions = this.isSkodaReturnPayment
      ? configuredCodes
          .map(code => ({
            value: code,
            label: this.getReasonCodeLabel(code),
          }))
      : [];

    const skodaReturnableAmount = this.skodaReturnableAmount;
    if (this.isSkodaReturnPayment) {
      // DEV-14262 changes availability only; the original SKODA payment cap still applies without an overpayment.
      this.leftToPay = skodaReturnableAmount;
      this.maxBenefitPaymentValue = skodaReturnableAmount;
    } else if (this.isReturn) {
      this.restoreOverallReturnLimit();
    } else {
      this.leftToPay = this.data.remainingToPay;
      // Non-benefit payments must not inherit benefit amount/percentage limits.
      this.maxBenefitPaymentValue = this.isBenefitPayment
        ? this._defaultMaxBenefitPaymentValue
        : Number.POSITIVE_INFINITY;
    }

    this.confirmForm.controls.skodaReturnCode.setValue(null, { emitEvent: false });
    this.confirmForm.controls.valueToPay.setValue(null, { emitEvent: false });
    this.updateSkodaReturnCodeValidation();
    this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });
  }

  private parseSkodaReturnCodes(rawCodes: unknown): string[] {
    let parsedCodes = rawCodes;

    if (typeof parsedCodes === "string") {
      try {
        parsedCodes = JSON.parse(parsedCodes);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(parsedCodes)) {
      return [];
    }

    return parsedCodes
      .filter((code): code is string => typeof code === "string")
      .map(code => code.trim())
      .filter(code => code.length > 0);
  }

  private updateSkodaReturnCodeValidation(): void {
    const control = this.confirmForm.controls.skodaReturnCode;
    control.setValidators(this.isSkodaReturnPayment ? [Validators.required] : []);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private handleSkodaReturnCodeSelection(code: string | null): void {
    if (!this.isSkodaReturnPayment) {
      return;
    }

    const prefilledAmount = code
      && OthersPaymentDialogComponent.SKODA_PREFILLED_RETURN_CODES.includes(code)
      ? this.skodaReturnableAmount
      : null;

    this.confirmForm.controls.valueToPay.setValue(prefilledAmount);
  }

  private restoreOverallReturnLimit(): void {
    if (!this.isReturn) {
      return;
    }

    const returnAmount = this.amountToReturn;
    this.leftToPay = -returnAmount;
    this.maxBenefitPaymentValue = returnAmount;
  }

  private getReasonCodeLabel(reasonCode: string): string {
    const translationKey = `webPos.${reasonCode}.ReasonCode`;
    const translation = this.translate.instant(translationKey);

    return typeof translation === "string" && translation !== translationKey
      ? translation
      : reasonCode;
  }

  override ngOnDestroy() {
    this.destroyObservable$.next(null);
    this.destroyObservable$.complete();
    super.ngOnDestroy?.();
  }

  openExternalPage() {
    if (this.paymentType) {
      const beneLinkAttr = this.paymentType.Attributes?.find(a => a.Key === 'BenefityWeb');
      window.open(beneLinkAttr.Value, 'benefityWeb');
    }
  }

  isBenefitFormInvalid(): boolean {
    if (this.isSkodaReturnPayment) {
      return this.confirmForm.controls.skodaReturnCode.invalid
        || this.confirmForm.controls.valueToPay.invalid;
    }

    if (this.selectedBenefitType == this.tenderTypes.Virtual) {
      return this.confirmForm.controls.confirmationCode.invalid || this.confirmForm.controls.valueToPay.invalid;
    }
    if (this.selectedBenefitType == this.tenderTypes.ElectronicVoucher ||
        this.selectedBenefitType == this.tenderTypes.Card) {
      if (this.isTerminalPaymentFormInvalid()) {
        return true;
      }

      if (!this._sobitService.allowPaymentByCard()) {
        console.error('Sobit payment not allowed');
        return true;
      }
      return false;
    }
    if (this.selectedBenefitType == this.tenderTypes.Voucher) {
      return this.confirmForm.controls.voucherCode.invalid || this.confirmForm.controls.valueToPay.invalid;
    }
    if (this.selectedBenefitType == this.tenderTypes.Cheque) {
      const hasNominals = this.selectedBenefitTypeNominalsOption && this.selectedBenefitTypeNominalsOption.length > 0;
      if (hasNominals) {
        const totalValue = this.getTotalPaperValue();
        return !(
          this.confirmForm.controls.voucherRowsGroup.controls.rows.valid
          && (totalValue <= this.maxPaymentValue || this.isOverpaymentOverrideEnabled())
        );
      } else {
        const valueToPay = Number(this.confirmForm.get('valueToPay')?.value);
        return !(
          Number.isFinite(valueToPay)
          && valueToPay > 0
          && (valueToPay <= this.maxPaymentValue || this.isOverpaymentOverrideEnabled())
        );
      }
    }
    return true;
  }

  public isOfflinePaymentFormInvalid(): boolean {
    // Offline mode bypasses only Sobit availability. Amount limits and the explicit
    // overpayment approval (including its required reason) remain mandatory.
    return this.selectedBenefitType !== TenderType.ElectronicVoucher
      || this.isTerminalPaymentFormInvalid();
  }

  private isTerminalPaymentFormInvalid(): boolean {
    const valueControl = this.confirmForm.controls.valueToPay;
    const overpaymentReasonControl = this.confirmForm.controls.overpaymentReason;
    const value = Number(valueControl.value);
    const exceedsLimitWithoutApproval = value > this.maxPaymentValue
      && !this.isOverpaymentOverrideEnabled();
    const approvedWithoutValidReason = this.isOverpaymentOverrideEnabled()
      && overpaymentReasonControl.invalid;

    return valueControl.invalid
      || !Number.isFinite(value)
      || value <= 0
      || exceedsLimitWithoutApproval
      || approvedWithoutValidReason;
  }

  getTotalPaperValue(): number {
    let total = 0;
    this.confirmForm.controls.voucherRowsGroup.controls.rows.value.forEach(row => {
      const value = Number(row.value);
      const numberOfItems = Number(row.numberOfItems);
      if (!isNaN(value) && !isNaN(numberOfItems) && value > 0 && numberOfItems > 0) {
        total += value * numberOfItems;
      }
    });
    return total;
  }

  addRow() {
    this.confirmForm.controls.voucherRowsGroup.controls.rows.push(new FormGroup({
      numberOfItems: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
      value: new FormControl<number | null>(null, [Validators.required]),
    }));

    const scrollContainer = document.getElementById('voucherRows');
    if (scrollContainer) {
      setTimeout(() => scrollContainer.scrollTo(0, scrollContainer.scrollHeight), 0);
    }
  }

  removeRow(index: number) {
    if (this.confirmForm.controls.voucherRowsGroup.controls.rows.length <= 1) {
      return;
    }
    this.confirmForm.controls.voucherRowsGroup.controls.rows.removeAt(index);
  }

  public validateMaxValue(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const value = Number(control.value);
      if (isNaN(value)) { return { notANumber: true }; }
      if (value > this.maxPaymentValue && !this.isOverpaymentOverrideEnabled()) { return { maxValueExceeded: true }; }
      return null;
    };
  }

  public validateSkodaReturnAmount(): ValidatorFn {
    return (control: FormControl): { [key: string]: boolean } | null => {
      if (!this.isSkodaReturnPayment) {
        return null;
      }

      const value = Number(control.value);
      return Number.isFinite(value) && value > 0
        ? null
        : { invalidSkodaReturnAmount: true };
    };
  }

  public get dialogTitleTranslationKey(): string {
    return this.isReturn
      ? "webPos.OthersPaymentReturnTitle"
      : "webPos.OthersPaymentTitle";
  }

  public get remainingAmountTranslationKey(): string {
    return this.isReturn
      ? "webPos.LeftToReturn"
      : "webPos.LeftToPay";
  }

  public get displayedLeftToPay(): number {
    return this.isReturn
      ? Math.abs(Number(this.leftToPay) || 0)
      : this.leftToPay;
  }

  public get isSkodaReturnPayment(): boolean {
    return this.isReturn
      && this.paymentType?.Name === SKODA_PAYMENT_NAME;
  }

  private get isBenefitPayment(): boolean {
    return this.paymentType?.IsBenefit === true;
  }

  private get maxPaymentValue(): number {
    if (this.isSkodaReturnPayment) {
      // Keep the SKODA payment cap even when DEV-14262 allows the dialog without an overall overpayment.
      return this.skodaReturnableAmount;
    }

    if (this.isReturn || this.isBenefitPayment) {
      return this._defaultMaxBenefitPaymentValue;
    }

    // Flight-ticket/eTravel dialogs intentionally have no reservation total
    // or remaining amount. Do not interpret null as zero and expose the
    // trip-specific overpayment approval in that flow.
    if (this.leftToPay === null || this.leftToPay === undefined) {
      return Number.POSITIVE_INFINITY;
    }

    const remainingToPay = Number(this.leftToPay);
    return Number.isFinite(remainingToPay) ? Math.max(0, remainingToPay) : Number.POSITIVE_INFINITY;
  }

  public get skodaReturnableAmount(): number {
    // Signed completed payments include negative refunds, which reduce the remaining SKODA cap.
    const amount = (this.data?.payments ?? [])
      .filter(payment =>
        payment.PaymentName?.trim().toUpperCase()
          === SKODA_PAYMENT_NAME
        && payment.Status === PaymentStatusEnum.Done
      )
      .reduce((sum, payment) => sum + (Number(payment.Amount) || 0), 0);

    return Math.max(0, Math.round(amount * 100) / 100);
  }
}
